import { useState, type VoidFunctionComponent } from 'react';

import { useParams, useQuery } from '../../../../contexts/RouterContext';
import { getProjectBaseURLFromParams } from '../../../../utils';
import { icons, IconText } from '../../../generic';
import { NavigationButton } from '../../../generic/Button';
import { useProject } from '../../contexts/ProjectContext';
import { useRevision as useRevisionContext } from '../../contexts/RevisionContext';
import RevisionDropdown from '../../DisplayProjectNav/RevisionDropdown/Dropdown';
import RevisionDisplay from '../../DisplayProjectNav/RevisionDropdown/RevisionDisplay';
import type { SnippetsQueryParams } from '../../../../../shared/snippets';
import type { Revision } from '../../../../types';
import type { RevisionRouteParams } from '../../projectDashboardTypes';

import styles from './Header.module.less';

/**
 * Header section for the snippet compare view.
 */
const SnippetsCompareHeader: VoidFunctionComponent = () => {
  // This project and revision data lives in Reflux.
  const project = useProject();
  const currentRevision = useRevisionContext();
  const baseRevision = currentRevision.data?.locator ?? '';

  // URL to take users "back" to the main snippets page.
  const routeParams = useParams<RevisionRouteParams>();
  const stopComparingURL = `${getProjectBaseURLFromParams(routeParams)}/browse/snippets`;

  const [queryParams, setQueryParams] = useQuery<SnippetsQueryParams>();

  // Track the currently-selected revision.
  const [selectedRevision, setSelectedRevision] = useState<Revision | undefined>(queryParams.olderRevisionLocator ? {
    locator: queryParams.olderRevisionLocator,
  } as Revision : undefined);

  return (
    <header className={styles.header}>
      <div className={styles.row}>
        <h2 className={styles.heading}>
          Comparing Snippets across revisions
        </h2>
        <NavigationButton
          to={stopComparingURL}
          type="anchor"
        >
          <IconText
            icon={icons.xCircle}
            text="Stop comparing"
          />
        </NavigationButton>
      </div>
      <div className={styles.compareRow}>
        <div className={styles.compareSection}>
          <span className={styles.compareLabel}>Compare:</span>
          <RevisionDisplay
            type="non-sbom"
            selectedRevision={currentRevision.data as Revision}
            variant="light"
          />
        </div>
        <div className={styles.compareSection}>
          <span className={styles.compareLabel}>Select a revision to compare:</span>
          <RevisionDropdown
            project={project}
            selectedRevision={selectedRevision}
            startingRevision={baseRevision}
            onSelectRevision={(revision) => {
              setQueryParams({ ...queryParams, olderRevisionLocator: revision.locator });
              setSelectedRevision(revision);
            }}
            placeholder="Select a revision to compare"
            variant="light"
            shouldFetchOnlyResolvedRevisions
            shouldFetchOnlyRevisionsWithSnippetAnalysis
          />
        </div>
      </div>
    </header>
  );
};

export default SnippetsCompareHeader;
